import React, { useMemo } from "react";
import { t } from "@lingui/macro";
import cx from "classnames";

import { USD_DECIMALS } from "config/factors";
import { formatAmount } from "lib/numbers";
import { FUNDING_RATE_PRECISION, LONG, SHORT } from "lib/legacy";
import { useLivePrice } from "lib/indexer/useLivePrices";
import { getNormalizedTokenSymbol, getPriceDecimals } from "config/tokens";

import "./MarketStatsBar.css";

type InfoToken = {
  symbol: string;
  isWrapped?: boolean;
  maxPrice?: bigint;
  minPrice?: bigint;
  fundingRate?: bigint;
  globalShortSize?: bigint;
  globalLongSize?: bigint;
  guaranteedUsd?: bigint;
};

type Props = {
  chainId: number;
  swapOption: string | undefined;
  toTokenAddress: string | undefined;
  infoTokens: Record<string, InfoToken>;
  nativeTokenAddress: string;
};

function formatFundingRate(rate: bigint | undefined): string {
  if (!rate) return "0.0000%";
  // rate is per position per funding interval (8h), multiply by 3 for 24h APR
  const rateNum = Number(rate) / FUNDING_RATE_PRECISION;
  const sign = rateNum >= 0 ? "+" : "";
  return `${sign}${(rateNum * 100).toFixed(4)}%`;
}

function formatOI(value: bigint | undefined): string {
  if (!value) return "$0";
  const n = Number(value) / 10 ** USD_DECIMALS;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

export function MarketStatsBar({ chainId, swapOption, toTokenAddress, infoTokens, nativeTokenAddress }: Props) {
  const isLong = swapOption === LONG;
  const isShort = swapOption === SHORT;

  const token: InfoToken | undefined = useMemo(() => {
    if (!toTokenAddress || !infoTokens) return undefined;
    return (
      infoTokens[toTokenAddress.toLowerCase()] ??
      Object.values(infoTokens).find((t) => t.symbol === infoTokens[toTokenAddress.toLowerCase()]?.symbol)
    );
  }, [toTokenAddress, infoTokens]);

  const symbol = token?.symbol ?? "";
  const normSymbol = getNormalizedTokenSymbol(symbol);
  const priceDecimals = getPriceDecimals(chainId, normSymbol);

  // Sub-second live price from WebSocket
  const livePrice = useLivePrice(normSymbol);
  const displayPrice = livePrice
    ? livePrice.toLocaleString("en-US", { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals })
    : token?.maxPrice
    ? formatAmount(token.maxPrice, USD_DECIMALS, priceDecimals, true)
    : "—";

  const markPrice = isLong ? token?.maxPrice : token?.minPrice;
  const markFormatted = markPrice ? formatAmount(markPrice, USD_DECIMALS, priceDecimals, true) : "—";

  // Funding rate (positive = longs pay shorts)
  const fundingRate = token?.fundingRate;
  const fundingFormatted = formatFundingRate(fundingRate);
  const fundingNegative = fundingRate !== undefined && fundingRate > 0n && isLong;
  const fundingPositive = fundingRate !== undefined && fundingRate > 0n && isShort;

  // Open interest. Synthetic longs are tracked in globalSyntheticLongSizes
  // (exposed as globalLongSize); guaranteedUsd is 0 for them. Fall back to
  // guaranteedUsd for non-synthetic (standard) markets.
  const longOI = token?.globalLongSize ?? token?.guaranteedUsd;
  const shortOI = token?.globalShortSize;

  if (!token) return null;

  return (
    <div className="MarketStatsBar">
      {/* Market label */}
      <div className="MSB-market">
        <span className="MSB-symbol">{normSymbol}/USD</span>
        {(isLong || isShort) && (
          <span className={cx("MSB-side-badge", { "MSB-side-badge--long": isLong, "MSB-side-badge--short": isShort })}>
            {isLong ? t`LONG` : t`SHORT`}
          </span>
        )}
      </div>

      <div className="MSB-divider" />

      {/* Live price */}
      <div className="MSB-stat">
        <span className="MSB-stat__label">{t`Price`}</span>
        <span className="MSB-stat__value MSB-price">${displayPrice}</span>
      </div>

      {/* Mark price */}
      <div className="MSB-stat">
        <span className="MSB-stat__label">{t`Mark`}</span>
        <span className="MSB-stat__value">${markFormatted}</span>
      </div>

      <div className="MSB-divider" />

      {/* Funding rate */}
      <div className="MSB-stat">
        <span className="MSB-stat__label">{t`Funding (8h)`}</span>
        <span
          className={cx("MSB-stat__value", {
            negative: fundingNegative,
            positive: fundingPositive,
            muted: !fundingNegative && !fundingPositive,
          })}
        >
          {fundingFormatted}
        </span>
      </div>

      <div className="MSB-divider" />

      {/* OI */}
      <div className="MSB-stat">
        <span className="MSB-stat__label">{t`Long OI`}</span>
        <span className="MSB-stat__value positive">{formatOI(longOI)}</span>
      </div>
      <div className="MSB-stat">
        <span className="MSB-stat__label">{t`Short OI`}</span>
        <span className="MSB-stat__value negative">{formatOI(shortOI)}</span>
      </div>
    </div>
  );
}
